'use client'

import { useEffect, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { usePermissions } from '@/hooks/usePermissions'
import type { PermissionExpr } from '@/lib/permissions/check'

interface IfHasPermissionProps {
  require: PermissionExpr
  fallback?: ReactNode
  children: ReactNode
}

export function IfHasPermission({ require: expr, fallback = null, children }: IfHasPermissionProps) {
  const { isRevalidating, hasPermission } = usePermissions()
  // Snapshot velho pode ter a permission que o back já revogou: esperar o /auth/me
  // troca "aparece e some" por "aparece só quando é certo".
  if (isRevalidating || !hasPermission(expr)) return <>{fallback}</>
  return <>{children}</>
}

interface ProtectedRouteProps {
  require: PermissionExpr
  redirectTo?: string
  loading?: ReactNode
  children: ReactNode
}

export function ProtectedRoute({ require: expr, redirectTo = '/chatbot', loading = null, children }: ProtectedRouteProps) {
  const { isLoading, isRevalidating, user, hasPermission } = usePermissions()
  const router = useRouter()
  const allowed = hasPermission(expr)
  const settling = isLoading || isRevalidating

  useEffect(() => {
    if (settling) return
    if (!user) {
      router.replace('/login')
      return
    }
    if (!allowed) router.replace(redirectTo)
  }, [settling, user, allowed, redirectTo, router])

  if (settling) return <>{loading}</>
  if (!allowed) return null
  return <>{children}</>
}
